import React, { useState, useEffect } from "react";
import { ColorPicker } from "primereact/colorpicker";
import { InputText } from "primereact/inputtext";

/**
 * AdvancedColorPicker
 * Allows picking color using pointer, HEX, or RGB input.
 */
const AdvancedColorPicker = ({ value = "#000000", onChange }) => {
  const [color, setColor] = useState(value);

  // Keep internal state synced with parent
  useEffect(() => {
    if (value !== color) setColor(value);
  }, [value]);

  const toHex = (c) => {
    if (!c) return "#000000";
    if (c.startsWith("rgb")) {
      const [r, g, b] = c.match(/\d+/g).map(Number);
      return (
        "#" +
        [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")
      ).toUpperCase();
    }
    return c.startsWith("#") ? c.toUpperCase() : "#" + c.toUpperCase();
  };

  const handleColorChange = (newColor) => {
    const hex = toHex(newColor);
    setColor(hex);
    onChange && onChange(hex);
  };

  const handleHexInput = (e) => {
    const val = e.target.value;
    setColor(val);

    // Only trigger change if it's a valid hex color
    if (/^#([0-9A-F]{3}){1,2}$/i.test(val)) {
      handleColorChange(val);
    }

    // If field is cleared or invalid, fallback to black
    if (val.trim() === "" || !/^#/.test(val)) {
      setColor("#000000");
      onChange && onChange("#000000");
    }
  };

  const handleRGBChange = (e, key) => {
    const val = Math.max(0, Math.min(255, Number(e.target.value) || 0));
    const rgbMatch = color.match(/[A-Fa-f0-9]{2}/g);
    const [r, g, b] = rgbMatch
      ? rgbMatch.map((x) => parseInt(x, 16))
      : [0, 0, 0];
    const updated = { r, g, b, [key]: val };
    const newHex =
      "#" +
      [updated.r, updated.g, updated.b]
        .map((x) => x.toString(16).padStart(2, "0"))
        .join("")
        .toUpperCase();
    handleColorChange(newHex);
  };

  // Safely extract RGB components — fallback to black if invalid
  const rgbMatch = color.match(/[A-Fa-f0-9]{2}/g);
  const [r, g, b] = rgbMatch ? rgbMatch.map((x) => parseInt(x, 16)) : [0, 0, 0];

  return (
    <div className="flex flex-row gap-8">
      {/* Picker Row */}
      <div className="flex align-items-center gap-3">
        <ColorPicker
          value={color}
          onChange={(e) => handleColorChange(e.value)}
          format="hex"
        />
      </div>

      {/* HEX Field */}
      <div className="flex align-items-center gap-2">
        <label>HEX:</label>
        <InputText
          value={color}
          onChange={handleHexInput}
          className="w-8rem"
          maxLength={7}
        />
      </div>

      {/* RGB Fields */}
      <div className="flex align-items-center gap-2">
        <label>RGB:</label>
        <InputText
          value={r}
          onChange={(e) => handleRGBChange(e, "r")}
          className="w-5rem"
        />
        <InputText
          value={g}
          onChange={(e) => handleRGBChange(e, "g")}
          className="w-5rem"
        />
        <InputText
          value={b}
          onChange={(e) => handleRGBChange(e, "b")}
          className="w-5rem"
        />
      </div>
    </div>
  );
};

export default AdvancedColorPicker;
